import { supabaseClient } from "@/lib/supabase";
import {
    type AiModelChoice,
    type AiProviderId,
    isSearchCapableAiChoice,
    listAiModelChoices,
} from "@/lib/ai/modelCatalog";

/**
 * All vendor calls go through the `public-ai-chat` Supabase edge function. API keys live there as
 * edge-function secrets and never reach the browser. `VITE_AI_PROVIDERS` (non-secret, comma-separated,
 * e.g. `deepseek,openai,gemini`) only tells the UI which providers to offer.
 */

const PROVIDER_LABELS: Record<AiProviderId, string> = {
    deepseek: "DeepSeek",
    openai: "OpenAI",
    gemini: "Gemini",
};

export interface ChatMessage {
    role: "system" | "user" | "assistant";
    content: string;
}

function configuredProviders(): Set<AiProviderId> {
    const raw = (import.meta.env.VITE_AI_PROVIDERS as string | undefined) ?? "";
    const result = new Set<AiProviderId>();
    for (const part of raw.split(",")) {
        const name = part.trim().toLowerCase();
        if (name === "deepseek" || name === "openai" || name === "gemini") result.add(name);
    }
    return result;
}

export function isDeepSeekConfigured(): boolean {
    return configuredProviders().has("deepseek");
}

export function isOpenAiConfigured(): boolean {
    return configuredProviders().has("openai");
}

export function isGeminiConfigured(): boolean {
    return configuredProviders().has("gemini");
}

export function isProviderConfigured(provider: AiProviderId): boolean {
    return configuredProviders().has(provider);
}

export function isAnyAiConfigured(): boolean {
    return configuredProviders().size > 0;
}

/** Models from the shared catalog whose providers are enabled in this environment. */
export function listConfiguredAiModelChoices(): AiModelChoice[] {
    return listAiModelChoices().filter((choice) => isProviderConfigured(choice.provider));
}

export function listConfiguredSearchAiModelChoices(): AiModelChoice[] {
    return listConfiguredAiModelChoices().filter((choice) => isSearchCapableAiChoice(choice));
}

export function isWebSearchConfigured(): boolean {
    return isOpenAiConfigured() || isGeminiConfigured();
}

export interface SearchGrounding {
    queries: string[];
    sources: Array<{ title: string; uri: string }>;
    searchCalls: number;
}

export type GeminiGrounding = SearchGrounding;

export interface SearchChatResult {
    content: string;
    grounding: SearchGrounding;
}

export type GeminiGenerateResult = SearchChatResult;

type EdgeBody = {
    mode: "chat" | "search";
    provider: AiProviderId;
    model?: string;
    messages: ChatMessage[];
    jsonMode?: boolean;
    googleSearch?: boolean;
};

type EdgeErrorBody = { error?: string; code?: string; provider?: string };

async function readEdgeError(error: unknown): Promise<EdgeErrorBody> {
    const context = (error as { context?: unknown } | null)?.context;
    if (context && typeof (context as Response).json === "function") {
        try {
            const body = (await (context as Response).json()) as EdgeErrorBody;
            if (body && typeof body === "object") return body;
        } catch {
            // fall through
        }
    }
    return {};
}

async function toAiError(error: unknown, provider: AiProviderId): Promise<Error> {
    if (error instanceof Error && error.name === "AbortError") return error;
    const { error: serverMessage, code } = await readEdgeError(error);
    const label = PROVIDER_LABELS[provider];
    switch (code) {
        case "not_configured":
            return new Error(serverMessage || `${label} is not configured on this environment.`);
        case "rate_limited":
            return new Error("Too many AI requests. Try again in a few minutes.");
        case "forbidden":
            return new Error("AI features are only available to staff.");
        case "mfa_required":
            return new Error("Complete two-factor sign-in to use AI features.");
        case "unauthorized":
            return new Error(serverMessage || "Sign in to use AI features.");
        default:
            return new Error(serverMessage || `${label} request failed. Please try again.`);
    }
}

async function invokeAiChat<T>(body: EdgeBody, signal?: AbortSignal): Promise<T> {
    const label = PROVIDER_LABELS[body.provider];
    if (!isProviderConfigured(body.provider)) {
        throw new Error(`${label} is not configured on this environment.`);
    }
    if (signal?.aborted) throw new DOMException("The operation was aborted.", "AbortError");

    const { data, error } = await supabaseClient.functions.invoke("public-ai-chat", { body, signal });
    if (error) throw await toAiError(error, body.provider);
    if (!data || typeof data !== "object") throw new Error("AI returned an empty response.");
    return data as T;
}

async function chatContent(body: Omit<EdgeBody, "mode">, signal?: AbortSignal): Promise<string> {
    const data = await invokeAiChat<{ content?: string }>({ ...body, mode: "chat" }, signal);
    if (!data.content) throw new Error("AI returned an empty response.");
    return data.content;
}

export async function deepseekChat(
    messages: ChatMessage[],
    jsonMode = true,
    signal?: AbortSignal,
    preferredModel?: string
): Promise<string> {
    return chatContent({ provider: "deepseek", model: preferredModel, messages, jsonMode }, signal);
}

export async function openAiChat(messages: ChatMessage[], jsonMode = true, preferredModel?: string): Promise<string> {
    return chatContent({ provider: "openai", model: preferredModel, messages, jsonMode });
}

export async function geminiChat(
    messages: ChatMessage[],
    jsonMode = true,
    preferredModel?: string,
    googleSearch = false
): Promise<string> {
    return chatContent({
        provider: "gemini",
        model: preferredModel,
        messages,
        jsonMode,
        ...(googleSearch ? { googleSearch: true } : {}),
    });
}

async function searchContent(provider: "openai" | "gemini", messages: ChatMessage[], model?: string) {
    const data = await invokeAiChat<Partial<SearchChatResult>>({ mode: "search", provider, model, messages });
    if (!data.content) throw new Error(`${PROVIDER_LABELS[provider]} returned an empty response.`);
    return {
        content: data.content,
        grounding: {
            queries: data.grounding?.queries ?? [],
            sources: data.grounding?.sources ?? [],
            searchCalls: data.grounding?.searchCalls ?? 0,
        },
    } satisfies SearchChatResult;
}

/** Gemini with Google Search, skipping flash-lite. Returns grounding so callers can see if it actually searched. */
export async function geminiSearchChat(messages: ChatMessage[], preferredModel?: string): Promise<SearchChatResult> {
    const preferred = preferredModel && !preferredModel.includes("lite") ? preferredModel : undefined;
    return searchContent("gemini", messages, preferred);
}

/** OpenAI Responses API with web_search. */
export async function openAiSearchChat(messages: ChatMessage[], preferredModel?: string): Promise<SearchChatResult> {
    return searchContent("openai", messages, preferredModel);
}

export async function searchChat(
    messages: ChatMessage[],
    options: { provider: "openai" | "gemini"; model?: string }
): Promise<SearchChatResult> {
    if (options.provider === "openai") {
        return openAiSearchChat(messages, options.model);
    }
    return geminiSearchChat(messages, options.model);
}

/** Call one catalog provider, trying the selected model first then that provider's fallbacks. */
export async function chatWithModel(
    messages: ChatMessage[],
    options: {
        provider: AiProviderId;
        model?: string;
        jsonMode?: boolean;
        signal?: AbortSignal;
        /** Gemini only: ground the answer with Google Search. */
        googleSearch?: boolean;
    }
): Promise<string> {
    const jsonMode = options.jsonMode ?? true;

    return chatContent(
        {
            provider: options.provider,
            model: options.model,
            messages,
            jsonMode,
            ...(options.provider === "gemini" && options.googleSearch ? { googleSearch: true } : {}),
        },
        options.signal
    );
}
